import type { AuthorityUiMetadata } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type NLPSearchResultSourceConnectorInfo } from '../ai-answer-dialog/types';

import { type NLPSearchDisclaimerType } from './nlp-search-disclaimer-type';
import { type StreamAnswerGenerationTypes } from './stream-answer-generation-types';

type ModelUsage = {
	total_tokens: number;
	prompt_tokens: number;
	completion_tokens: number;
	request_count: number;
	total_cost: number;
	duration: number;
};

type Usage = {
	model_usage: {
		[key: string]: ModelUsage;
	};
	total: ModelUsage;
};

type Durations = {
	events: {
		[key: string]: number;
	};
	total: number;
};

export type Source = {
	ari: string;
	title: string;
	type: string;
	product: string | null;
	url: string;
	lastModified: string;
	message_id: number;
	id: number;
	connectorInfo?: NLPSearchResultSourceConnectorInfo;
};

export type Sources = Array<Source>;

export type PluginInvocation = {
	id: number | null;
	plugin_name: string | null;
	plugin_invocation_status: `PLUGIN_STARTED` | `PLUGIN_FINISHED` | null;
	plugin_invocation_time: string | null;
	plugin_description: string | null;
	plugin_input: string | null;
	plugin_output: string | null;
	plugin_error_message: string | null;
};

export type PluginInvocationMessage = PluginInvocation & {
	message_id: number | null;
};

export type MinionOutput = {
	minion_id: string;
	minion_input: string;
	minion_output: string;
	minion_execution_duration: number;
	minion_extra_info: string;
};

export type Metadata = {
	bm25Variant: string;
	confidenceScore: number;
	isFallbackMessage: boolean;
	originalQuery: string;
	semanticSearchLocationVariant: string;
	semanticSearchVariant: string;
	disclaimer: NLPSearchDisclaimerType | null;
	request_id?: string;
	global_cache_info?: {
		cache_hit?: boolean;
		duration_ms?: number;
	};
};

export type Appendix = {
	type: 'requestForm' | 'helpDesk';
	content: string;
	appendix_sources?: Sources;
};

export type Appendices = Array<Appendix>;

export type Message = {
	role: string;
	content: string;
	id: number;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	sources?: Sources;
	message_metadata?: Metadata;
	author?: {
		actor_type: string;
		ari: string | null;
		id: string;
		name: string;
		named_id: string;
	};
	author_id?: string;
	experience_id?: string;
	conversation_channel_id?: string;
	appendices?: Appendices;
	content_mime_type: string;
	minion_outputs?: {
		minion_id: string;
		minion_input: string;
		minion_output: string;
		minion_execution_duration: number;
		minion_extra_info: string;
	}[];
	authority_ui_metadata?: AuthorityUiMetadata | null;
};

type AssistantMessage = Message & {
	role: 'ASSISTANT';
};

export type AgentError = {
	content: string;
	message_template: 'NO_ANSWER_LLM' | 'NO_ANSWER_PEOPLE';
};

export type AgentResponse = {
	message: AssistantMessage;
	usage_data?: Usage;
	durations_data?: Durations;
	sources?: Sources;
	human_message?: {
		content?: string;
	};
};

export type StreamResponse = {
	type: 'FINAL_RESPONSE';
	message: AgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamError = {
	type: 'ERROR';
	message: {
		content: string;
		status_code?: number;
		message_template?: string;
	};
	millisOffset: number;
	metadata: null | {
		error_message?: string;
		request_id?: string;
		timeout?: number;
	};
};

export type StreamAnswerPart = {
	type: 'ANSWER_PART';
	message: { content: string; role: 'ASSISTANT' };
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
	};
};

export type StreamGenerationType = {
	type: 'ANSWER_GENERATION_TYPE';
	message: { answer_generation_type: StreamAnswerGenerationTypes };
	millisOffset: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamTrace = {
	type: 'TRACE';
	message: {
		// Legacy trace content (always present).
		content: string;
		// Fields below are present only on rich research-step traces
		// returned by the BE when X-Think-Deeper-Messages-Opt-In is set.
		message_template?: string;
		title?: string;
		stepId?: string;
		roundId?: number;
		totalRoundId?: number | null;
		sources?: Sources | null;
		skill_status_metadata?: Record<string, unknown>;
		toolName?: string | null;
		toolIcon?: string | null;
		product?: string | null;
		url?: string | null;
		products?: string[] | null;
		user_query?: string | null;
		formulated_query?: string | null;
	};
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamPluginInvocation = {
	type: 'PLUGIN_INVOCATION';
	message: PluginInvocation;
	millisOffset: number;
	metadata?: { run_id: string; request_id: string };
};

export type StreamFollowUp = {
	type: 'FOLLOW_UP_QUERIES';
	message: {
		follow_up_queries: string[] | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamErrorStateQuerySuggestions = {
	type: 'ERROR_STATE_QUERY_SUGGESTIONS';
	message: {
		suggested_queries: string[] | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamDynamicLayoutSubAgent = {
	type: 'DL_SUB_AGENT';
	message: any;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamEarlyStop = {
	type: 'EARLY_STOP';
	message?: {
		reason?: string;
	};
};

export type StreamMessage =
	| StreamGenerationType
	| StreamTrace
	| StreamPluginInvocation
	| StreamAnswerPart
	| StreamResponse
	| StreamError
	| StreamFollowUp
	| StreamErrorStateQuerySuggestions
	| StreamDynamicLayoutSubAgent
	| StreamEarlyStop;

export const isStreamResponse = (message: any): message is StreamResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamError = (message: any): message is StreamError =>
	message instanceof Object && message.type === 'ERROR';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamAnswerPart = (message: any): message is StreamAnswerPart =>
	message instanceof Object && message.type === 'ANSWER_PART';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamGenerationType = (message: any): message is StreamGenerationType =>
	message instanceof Object && message.type === 'ANSWER_GENERATION_TYPE';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamTrace = (message: any): message is StreamTrace =>
	message instanceof Object && message.type === 'TRACE';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamPluginInvocation = (message: any): message is StreamPluginInvocation =>
	message instanceof Object && message.type === 'PLUGIN_INVOCATION';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamFollowUp = (message: any): message is StreamFollowUp =>
	message instanceof Object && message.type === 'FOLLOW_UP_QUERIES';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamErrorStateQuerySuggestions = (
	message: any,
): message is StreamErrorStateQuerySuggestions =>
	message instanceof Object && message.type === 'ERROR_STATE_QUERY_SUGGESTIONS';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamDynamicLayoutSubAgent = (
	message: any,
): message is StreamDynamicLayoutSubAgent =>
	message instanceof Object && message.type === 'DL_SUB_AGENT';

// ---------------------------------------------------------------
// Separated SAIN Types

export type SAINMessage = {
	role: string;
	content: string;
	id: number;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	sources?: Sources;
	message_metadata?: Metadata;
	author?: {
		actor_type: string;
		ari: string | null;
		id: string;
		name: string;
		named_id: string;
	};
	author_id?: string;
	experience_id?: string;
	conversation_channel_id?: string;
	appendices?: Appendices;
	content_mime_type: string;
	minion_outputs?: MinionOutput[];
	authority_ui_metadata?: AuthorityUiMetadata | null;
};

type SAINAssistantMessage = SAINMessage;

export type SAINAgentResponse = {
	message: SAINAssistantMessage;
	usage_data?: Usage;
	durations_data?: Durations;
	sources?: Sources;
	human_message?: {
		content?: string;
	};
};

export type StreamSAINResponse = {
	type: 'FINAL_RESPONSE';
	message: SAINAgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamSAINMessage =
	| StreamGenerationType
	| StreamTrace
	| StreamPluginInvocation
	| StreamAnswerPart
	| StreamSAINResponse
	| StreamError
	| StreamFollowUp
	| StreamErrorStateQuerySuggestions
	| StreamDynamicLayoutSubAgent
	| StreamEarlyStop;

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamSAINResponse = (message: any): message is StreamSAINResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamEarlyStop = (message: any): message is StreamEarlyStop =>
	message instanceof Object && message.type === 'EARLY_STOP';

// ---------------------------------------------------------------
// Separated Definition Types

export type DefinitionAgentResponse = {
	content: string;
	usage_data?: Usage;
	durations_data?: Durations;
	sources?: Sources;
	human_message?: {
		content?: string;
	};
};

export type StreamDefinitionResponse = {
	type: 'FINAL_RESPONSE';
	message: DefinitionAgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamDefinitionMessage =
	| StreamTrace
	| StreamPluginInvocation
	| StreamAnswerPart
	| StreamDefinitionResponse
	| StreamError
	| StreamFollowUp
	| StreamDynamicLayoutSubAgent;

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const isStreamDefinitionResponse = (message: any): message is StreamDefinitionResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';
